import isNil from 'lodash/isNil'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import reduce from 'lodash/reduce'
import * as React from 'react'
import {maybe, fromMaybe} from '@freckle/maybe'
import {exhaustive} from '@freckle/exhaustive'
import {isNullOrUndefinedOrEmptyString} from '@frontrow/console/src/entities/helpers/common-helper'
import {
  type IdentityProviderT,
  IdentityProvider
} from '@frontrow/console/src/entities/helpers/identity-provider'
import {isSchoolCrmSynced, type StarStatus} from '@frontrow/console/src/entities/models/school'
import AgentContext from '@frontrow/console/src/context/agent'
import {isSuperUser} from '@frontrow/console/src/components/common/restricted'
import {type PolyfilledEvent} from '@frontrow/console/src/polyfills'

// Models
import {
  detailedToBasicSchool,
  type BasicSchoolT,
  type DetailedSchoolT,
  type RenaissanceSchoolClientT,
  type WeakRenaissanceSchoolClientT,
  type BasicSchoolWithRenaissanceClientT
} from '@frontrow/console/src/models/school'
// Components
import UnsavedChangesModal from '@frontrow/console/src/components/common/unsaved-changes'
import EditSchoolForm from '@frontrow/console/src/components/pages/edit-school-page/edit-school-form/edit-school-form'
// Helpers
import {type AddressT} from '@frontrow/console/src/helpers/addresses'
import {
  validateRenaissanceSchoolClient,
  type ValidationResultT,
  renaissanceFieldsRequired
} from '@frontrow/console/src/helpers/validations/school'

type Props = {
  onSubmit: (args: {
    updatedSchool: BasicSchoolWithRenaissanceClientT
    shouldAddFakeDistrict: boolean
    shouldDeleteCleverAndClasslinkAssociations: boolean
  }) => void
  schoolToUpdate: DetailedSchoolT
}

type RenaissanceSchoolT = {
  renaissanceSchoolClientClientId: string | undefined | null
  renaissanceSchoolClientRpid: string | undefined | null
  renaissanceSchoolClientBaseUrl: string | undefined | null
  renaissanceSchoolClientPlatform: string
  renaissanceSchoolClientStarStatus: StarStatus
  renaissanceSchoolClientSharedRosteringEnabled: boolean
}

type DerivedStateT = {
  school: BasicSchoolT
  shouldAddFakeDistrict: boolean
  showStarSyncFreshlyEnabledNotification: boolean
  shouldMuteLicenseUtilizationNotifications: boolean
  shouldDeleteCleverAndClasslinkAssociations: boolean
  syncPaused: boolean
  selectedIdp: IdentityProviderT | undefined | null
} & RenaissanceSchoolT

function deriveState(schoolToUpdate: DetailedSchoolT): DerivedStateT {
  const classLinkId = schoolToUpdate.classLinkSchoolId

  const schoolValue = {
    ...schoolToUpdate,
    courseSubjectsByLicensing: false
  }

  const defaultRenaissanceSchoolClient = {
    renaissanceSchoolClientPlatform: 'renaissance_growth_platform',
    renaissanceSchoolClientRpid: null as string | null,
    renaissanceSchoolClientClientId: null as string | null,
    renaissanceSchoolClientBaseUrl: null as string | null,
    renaissanceSchoolClientStarStatus: 'star_unset' as StarStatus,
    renaissanceSchoolClientSharedRosteringEnabled: false
  }

  const renaissanceSchoolClientState: RenaissanceSchoolT = maybe(
    () => defaultRenaissanceSchoolClient,
    renaissanceSchoolClient => ({
      renaissanceSchoolClientPlatform: renaissanceSchoolClient.platform,
      renaissanceSchoolClientClientId: renaissanceSchoolClient.clientId,
      renaissanceSchoolClientRpid: renaissanceSchoolClient.rpid,
      renaissanceSchoolClientBaseUrl: renaissanceSchoolClient.baseUrl,
      renaissanceSchoolClientStarStatus: renaissanceSchoolClient.starStatus,
      renaissanceSchoolClientSharedRosteringEnabled: renaissanceSchoolClient.sharedRosteringEnabled
    }),
    schoolToUpdate.renaissanceSchoolClient
  )

  return {
    school: {
      ...detailedToBasicSchool(schoolValue),
      classLinkSourceId: isNil(classLinkId) ? null : classLinkId.slice(classLinkId.indexOf(':') + 1)
    },
    shouldAddFakeDistrict: canAddFakeDistrict(schoolToUpdate),
    showStarSyncFreshlyEnabledNotification: false,
    shouldMuteLicenseUtilizationNotifications: !isNil(
      schoolToUpdate.licenseUtilizationNotificationsMutedAt
    ),
    shouldDeleteCleverAndClasslinkAssociations: false,
    syncPaused: !schoolToUpdate.syncIdp,
    selectedIdp: schoolValue.activeIdp,
    ...renaissanceSchoolClientState
  }
}

export default function EditSchoolFormContainer(props: Props): React.ReactElement {
  const {onSubmit, schoolToUpdate} = props

  const initialState = React.useMemo(() => deriveState(schoolToUpdate), [schoolToUpdate])

  const [school, setSchool] = React.useState<BasicSchoolT>(initialState.school)
  const [shouldAddFakeDistrict, setShouldAddFakeDistrict] = React.useState<boolean>(
    initialState.shouldAddFakeDistrict
  )
  const [shouldMuteLicenseUtilizationNotifications, setShouldMuteLicenseUtilizationNotifications] =
    React.useState<boolean>(initialState.shouldMuteLicenseUtilizationNotifications)
  const [
    shouldDeleteCleverAndClasslinkAssociations,
    setShouldDeleteCleverAndClasslinkAssociations
  ] = React.useState<boolean>(initialState.shouldDeleteCleverAndClasslinkAssociations)
  const [showStarSyncFreshlyEnabledNotification, setShowStarSyncFreshlyEnabledNotification] =
    React.useState<boolean>(initialState.showStarSyncFreshlyEnabledNotification)
  const [syncPaused, setSyncPaused] = React.useState<boolean>(initialState.syncPaused)
  const [selectedIdp, setSelectedIdp] = React.useState<IdentityProviderT | undefined | null>(
    initialState.selectedIdp
  )

  // Renaissance School Client state
  const [renaissanceSchoolClientClientId, setRenaissanceSchoolClientClientId] = React.useState<
    string | undefined | null
  >(initialState.renaissanceSchoolClientClientId)
  const [renaissanceSchoolClientRpid, setRenaissanceSchoolClientRpid] = React.useState<
    string | undefined | null
  >(initialState.renaissanceSchoolClientRpid)
  const [renaissanceSchoolClientBaseUrl, setRenaissanceSchoolClientBaseUrl] = React.useState<
    string | undefined | null
  >(initialState.renaissanceSchoolClientBaseUrl)
  const [renaissanceSchoolClientPlatform, setRenaissanceSchoolClientPlatform] =
    React.useState<string>(initialState.renaissanceSchoolClientPlatform)
  const [renaissanceSchoolClientStarStatus, setRenaissanceSchoolClientStarStatus] =
    React.useState<StarStatus>(initialState.renaissanceSchoolClientStarStatus)
  const [
    renaissanceSchoolClientSharedRosteringEnabled,
    setRenaissanceSchoolClientSharedRosteringEnabled
  ] = React.useState<boolean>(initialState.renaissanceSchoolClientSharedRosteringEnabled)

  // Compute isDirty by comparing current state with initial state
  const isDirty = React.useMemo(() => {
    return (
      !isEqual(school, initialState.school) ||
      shouldAddFakeDistrict !== initialState.shouldAddFakeDistrict ||
      shouldMuteLicenseUtilizationNotifications !==
        initialState.shouldMuteLicenseUtilizationNotifications ||
      shouldDeleteCleverAndClasslinkAssociations !==
        initialState.shouldDeleteCleverAndClasslinkAssociations ||
      showStarSyncFreshlyEnabledNotification !==
        initialState.showStarSyncFreshlyEnabledNotification ||
      syncPaused !== initialState.syncPaused ||
      selectedIdp !== initialState.selectedIdp ||
      renaissanceSchoolClientClientId !== initialState.renaissanceSchoolClientClientId ||
      renaissanceSchoolClientRpid !== initialState.renaissanceSchoolClientRpid ||
      renaissanceSchoolClientBaseUrl !== initialState.renaissanceSchoolClientBaseUrl ||
      renaissanceSchoolClientPlatform !== initialState.renaissanceSchoolClientPlatform ||
      renaissanceSchoolClientStarStatus !== initialState.renaissanceSchoolClientStarStatus ||
      renaissanceSchoolClientSharedRosteringEnabled !==
        initialState.renaissanceSchoolClientSharedRosteringEnabled
    )
  }, [
    school,
    shouldAddFakeDistrict,
    shouldMuteLicenseUtilizationNotifications,
    shouldDeleteCleverAndClasslinkAssociations,
    showStarSyncFreshlyEnabledNotification,
    syncPaused,
    selectedIdp,
    renaissanceSchoolClientClientId,
    renaissanceSchoolClientRpid,
    renaissanceSchoolClientBaseUrl,
    renaissanceSchoolClientPlatform,
    renaissanceSchoolClientStarStatus,
    renaissanceSchoolClientSharedRosteringEnabled,
    initialState
  ])

  const getWeakRenaissanceSchoolClientFromState = (): WeakRenaissanceSchoolClientT => {
    return {
      platform: renaissanceSchoolClientPlatform,
      clientId: renaissanceSchoolClientClientId,
      rpid: renaissanceSchoolClientRpid,
      baseUrl: renaissanceSchoolClientBaseUrl,
      starStatus: renaissanceSchoolClientStarStatus,
      sharedRosteringEnabled: renaissanceSchoolClientSharedRosteringEnabled
    }
  }

  const onSubmitHandler = (e: PolyfilledEvent) => {
    e.preventDefault()

    const latLong = school.latLong

    const updatedLatLong =
      latLong && !isNaN(latLong.latitude!) && !isNaN(latLong.longitude!) ? latLong : null

    const renaissanceSchoolClientValidationResult: ValidationResultT =
      validateRenaissanceSchoolClient(getWeakRenaissanceSchoolClientFromState())

    const renaissanceSchoolClient: RenaissanceSchoolClientT | undefined | null = fromValidated(
      renaissanceSchoolClientValidationResult
    )

    const cleverSchoolId =
      isNil(selectedIdp) || selectedIdp === 'clever' ? school.cleverSchoolId : null
    const classLinkSourceId =
      isNil(selectedIdp) || selectedIdp === 'class-link' ? school.classLinkSourceId : null

    const activeIdp = selectedIdp
    const syncIdp = !syncPaused

    onSubmit({
      updatedSchool: {
        ...school,
        activeIdp,
        syncIdp,
        cleverSchoolId,
        classLinkSourceId,
        latLong: updatedLatLong,
        licenseUtilizationNotificationsMutedAt: shouldMuteLicenseUtilizationNotifications
          ? fromMaybe(() => new Date(), school.licenseUtilizationNotificationsMutedAt)
          : null,
        renaissanceSchoolClient
      },
      shouldAddFakeDistrict,
      shouldDeleteCleverAndClasslinkAssociations
    })
  }

  const onToggleShouldAddFakeDistrict = () => {
    const newShouldAddFakeDistrict = !shouldAddFakeDistrict
    setShouldAddFakeDistrict(newShouldAddFakeDistrict)
    setSchool(prevSchool => ({...prevSchool, districtId: NaN}))
  }

  const onUpdateStarStatus = (starStatus: StarStatus) => {
    const hasFreshlyEnabledStarSync = didFreshlyEnableStarSync(schoolToUpdate, starStatus)
    setRenaissanceSchoolClientStarStatus(starStatus)
    setShowStarSyncFreshlyEnabledNotification(hasFreshlyEnabledStarSync)
  }

  const onToggleShouldMuteLicenseUtilizationNotifications = () => {
    setShouldMuteLicenseUtilizationNotifications(prev => !prev)
  }

  const onChangeSchoolName = (name: string) => {
    setSchool(prevSchool => ({...prevSchool, name}))
  }

  const onChangeCrmId = (crmId: string) => {
    setSchool(prevSchool => ({
      ...prevSchool,
      crmId: isEmpty(crmId) ? null : crmId
    }))
  }

  const onChangeClassLinkSourceId = (classLinkSourceId: string) => {
    setSchool(prevSchool => ({...prevSchool, classLinkSourceId}))
  }

  const onChangeCleverSchoolId = (cleverSchoolId: string) => {
    setSchool(prevSchool => ({...prevSchool, cleverSchoolId}))
  }

  const onChangeAddress = (address: AddressT) => {
    setSchool(prevSchool => ({...prevSchool, ...address}))
  }

  const onChangeSchoolSisId = (sisId: string) => {
    setSchool(prevSchool => ({...prevSchool, sisId}))
  }

  const onTogglePauseSync = () => {
    setSyncPaused(prev => !prev)
  }

  const onChangeLatitude = (latitude: string) => {
    setSchool(prevSchool => {
      const latLong = {...prevSchool.latLong, latitude: parseFloat(latitude)}
      return {...prevSchool, latLong}
    })
  }

  const onChangeLongitude = (longitude: string) => {
    setSchool(prevSchool => {
      const latLong = {
        ...prevSchool.latLong,
        longitude: parseFloat(longitude)
      }
      return {...prevSchool, latLong}
    })
  }

  const onChangeDistrictId = (districtIdText: string) => {
    setShouldAddFakeDistrict(false)
    setSchool(prevSchool => ({
      ...prevSchool,
      districtId: parseInt(districtIdText, 10)
    }))
  }

  const onChangeRenaissancePlatform = (e: PolyfilledEvent) => {
    if (e.target instanceof HTMLInputElement) {
      setRenaissanceSchoolClientPlatform(e.target.value)
    }
  }

  const onChangeClientId = (clientId: string) => {
    setRenaissanceSchoolClientClientId(clientId)
  }

  const onChangeRpid = (rpid: string) => {
    setRenaissanceSchoolClientRpid(rpid)
  }

  const onChangeRpIdentifier = (renaissanceRpIdentifier: string) => {
    setSchool(prevSchool => ({...prevSchool, renaissanceRpIdentifier}))
  }

  const onChangeBaseUrl = (baseUrl: string) => {
    setRenaissanceSchoolClientBaseUrl(baseUrl)
  }

  const onChangeIdp = (idp?: IdentityProviderT | null) => {
    const shouldDeleteCleverAndClasslinkAssociationsNew = idp !== schoolToUpdate.activeIdp
    const renaissanceSchoolClientSharedRosteringEnabledNew = idp === 'renaissance'

    // When switching from no idp to an idp managed state, unpause the sync by default
    const syncPausedNew = isNil(selectedIdp) && !isNil(idp) ? false : syncPaused
    const resetIdpValues = makeResetIdpValues(idp)

    setSchool(prevSchool => resetIdpValues(prevSchool))
    setShouldDeleteCleverAndClasslinkAssociations(shouldDeleteCleverAndClasslinkAssociationsNew)
    setRenaissanceSchoolClientSharedRosteringEnabled(
      renaissanceSchoolClientSharedRosteringEnabledNew
    )
    setSelectedIdp(idp)
    setSyncPaused(syncPausedNew)
  }

  const weakRenaissanceSchoolClient = getWeakRenaissanceSchoolClientFromState()
  const renaissanceSchoolClientValidationResult: ValidationResultT =
    validateRenaissanceSchoolClient(weakRenaissanceSchoolClient)

  const missingRenaissanceClientConfig =
    isNullOrUndefinedOrEmptyString(renaissanceSchoolClientPlatform) ||
    isNullOrUndefinedOrEmptyString(renaissanceSchoolClientBaseUrl) ||
    isNullOrUndefinedOrEmptyString(renaissanceSchoolClientRpid)

  const needsStarIntegrationFields =
    renaissanceSchoolClientStarStatus === 'star_enabled' && missingRenaissanceClientConfig

  const needsRenaissanceIdPFields =
    selectedIdp === 'renaissance' &&
    (missingRenaissanceClientConfig ||
      renaissanceSchoolClientPlatform !== 'renaissance_growth_platform')

  const submitDisabledReason = firstSubmitBlocker({
    needsStarIntegrationFields,
    needsRenaissanceIdPFields,
    renaissanceConfigInvalid: renaissanceSchoolClientValidationResult.tag === 'invalid',
    countryMissing: school.countryCode === '',
    districtMissing: !shouldAddFakeDistrict && isNaN(school.districtId)
  })

  const crmManaged = isSchoolCrmSynced(schoolToUpdate)

  return (
    <AgentContext.Consumer>
      {agent => {
        return (
          <React.Fragment>
            <EditSchoolForm
              //School Details
              school={school}
              schoolToUpdate={schoolToUpdate}
              //Form features
              canAddFakeDistrict={canAddFakeDistrict(schoolToUpdate)}
              canUpdateCrmId={isSuperUser(agent)}
              shouldAddFakeDistrict={shouldAddFakeDistrict}
              shouldMuteLicenseUtilizationNotifications={shouldMuteLicenseUtilizationNotifications}
              shouldDeleteCleverAndClasslinkAssociations={
                shouldDeleteCleverAndClasslinkAssociations
              }
              syncPaused={syncPaused}
              selectedIdp={selectedIdp}
              crmManaged={crmManaged}
              //Renaissance Client
              renaissanceSchoolClientClientId={renaissanceSchoolClientClientId}
              renaissanceSchoolClientRpid={renaissanceSchoolClientRpid}
              renaissanceSchoolClientBaseUrl={renaissanceSchoolClientBaseUrl}
              renaissanceSchoolClientPlatform={renaissanceSchoolClientPlatform}
              renaissanceSchoolClientStarStatus={renaissanceSchoolClientStarStatus}
              //Form validations
              needsStarIntegrationFields={needsStarIntegrationFields}
              needsRenaissanceIdPFields={needsRenaissanceIdPFields}
              renaissanceFieldsRequired={renaissanceFieldsRequired(
                selectedIdp === 'renaissance',
                weakRenaissanceSchoolClient
              )}
              showStarSyncFreshlyEnabledNotification={showStarSyncFreshlyEnabledNotification}
              //Submit
              submitDisabledReason={submitDisabledReason}
              onSubmit={onSubmitHandler}
              //Form Events
              onChangeAddress={onChangeAddress}
              onChangeBaseUrl={onChangeBaseUrl}
              onChangeClassLinkSourceId={onChangeClassLinkSourceId}
              onChangeCleverSchoolId={onChangeCleverSchoolId}
              onChangeClientId={onChangeClientId}
              onChangeDistrictId={onChangeDistrictId}
              onChangeIdp={onChangeIdp}
              onChangeLatitude={onChangeLatitude}
              onChangeLongitude={onChangeLongitude}
              onChangeRenaissancePlatform={onChangeRenaissancePlatform}
              onChangeRpIdentifier={onChangeRpIdentifier}
              onChangeRpid={onChangeRpid}
              onChangeSchoolName={onChangeSchoolName}
              onChangeCrmId={onChangeCrmId}
              onChangeSchoolSisId={onChangeSchoolSisId}
              onToggleShouldAddFakeDistrict={onToggleShouldAddFakeDistrict}
              onToggleShouldMuteLicenseUtilizationNotifications={
                onToggleShouldMuteLicenseUtilizationNotifications
              }
              onUpdateStarStatus={onUpdateStarStatus}
              onTogglePauseSync={onTogglePauseSync}
            />
            <UnsavedChangesModal when={isDirty} />
          </React.Fragment>
        )
      }}
    </AgentContext.Consumer>
  )
}

// The submit button is disabled by five separate conditions and used to give no
// reason for it. Returns the first one that applies, or null when the form can
// be submitted.
function firstSubmitBlocker(blockers: {
  needsStarIntegrationFields: boolean
  needsRenaissanceIdPFields: boolean
  renaissanceConfigInvalid: boolean
  countryMissing: boolean
  districtMissing: boolean
}): string | null {
  if (blockers.countryMissing) {
    return 'Select a country before saving.'
  }
  if (blockers.districtMissing) {
    return 'Enter a district ID, or choose to create a fake district.'
  }
  if (blockers.needsRenaissanceIdPFields) {
    return 'The Renaissance IdP needs RGP as the platform, plus a Client Id, RPID and Base URL.'
  }
  if (blockers.needsStarIntegrationFields) {
    return 'Star integration needs an RPID and a Base URL.'
  }
  if (blockers.renaissanceConfigInvalid) {
    return 'The Renaissance configuration is incomplete: fill in Client Id, RPID and Base URL, or clear all three.'
  }
  return null
}

function fromValidated(
  renaissanceSchoolClientValidationResult: ValidationResultT
): RenaissanceSchoolClientT | undefined | null {
  switch (renaissanceSchoolClientValidationResult.tag) {
    case 'valid':
      return renaissanceSchoolClientValidationResult.result
    case 'invalid':
    case 'nonexistent':
      return null
    default:
      return exhaustive(renaissanceSchoolClientValidationResult, 'ValidationResultT')
  }
}

function canAddFakeDistrict(schoolToUpdate: DetailedSchoolT): boolean {
  return schoolToUpdate.districtId === null || schoolToUpdate.districtId === undefined
}

function didFreshlyEnableStarSync(school: DetailedSchoolT, currentState: StarStatus): boolean {
  if (school.renaissanceSchoolClient !== null && school.renaissanceSchoolClient !== undefined) {
    const {renaissanceSchoolClient} = school
    const {starStatus} = renaissanceSchoolClient

    return starStatus !== 'star_enabled' && currentState === 'star_enabled'
  }

  return false
}

function makeResetIdpValues(
  currentIdp?: IdentityProviderT | null
): (a: BasicSchoolT) => BasicSchoolT {
  return reduce(
    IdentityProvider.all(),
    (acc, idp) => {
      if (currentIdp !== idp) {
        return x => resetIdpValue(idp)(acc(x))
      }
      return acc
    },
    x => x
  )
}

function resetIdpValue(idp: IdentityProviderT): (a: BasicSchoolT) => BasicSchoolT {
  switch (idp) {
    case 'clever':
      return school => ({...school, cleverSchoolId: null})
    case 'csv':
      return x => x
    case 'class-link':
      // The server resolves `classLinkSourceId` into `classLinkSchoolId`.
      return school => ({...school, classLinkSourceId: null})
    case 'renaissance':
      return x => x
    default:
      return exhaustive(idp, 'idp')
  }
}
